import MailListItem from './MailListItem';
import { hasAttachments, important } from './MailListItemContainer.locstring.json';
import getBackgroundColorClass from '../selectors/getBackgroundColorClass';
import getIsChecked from '../selectors/getIsChecked';
import getUnreadItemClass from '../selectors/getUnreadItemClass';
import onMailListItemClickHandler from '../utils/onMailListItemClickHandler';
import MailListItemDataProps from '../utils/types/MailListItemDataProps';
import MailListTableProps from '../utils/types/MailListTableProps';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableQuery } from 'owa-group-utils';
import { keydownHandler } from 'owa-hotkeys';
import loc from 'owa-localize';
import { draftSenderPersonaPlaceholder } from 'owa-locstrings/lib/strings/draftsenderpersonaplaceholder.locstring.json';
import { flaggedFilter } from 'owa-locstrings/lib/strings/flaggedfilter.locstring.json';
import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import { pinnedGroupHeader } from 'owa-locstrings/lib/strings/pinnedgroupheader.locstring.json';
import { unreadFilter } from 'owa-locstrings/lib/strings/unreadfilter.locstring.json';
import { keyboardCollapseConversation, singleSelectRow } from 'owa-mail-actions';
import { tabIndex } from 'owa-mail-focus-manager';
import { getCommands } from 'owa-mail-hotkeys';
import { showMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';
import { TableQueryType } from 'owa-mail-list-store';
import isMailRowExpanded from 'owa-mail-list-store/lib/selectors/isConversationExpanded';
import MailListContextMenuSource from 'owa-mail-list-store/lib/store/schema/MailListContextMenuSource';
import MailListContextMenuType from 'owa-mail-list-store/lib/store/schema/MailListContextMenuType';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { MailListItemExpansion } from 'owa-mail-list-view';
import { mailStore } from 'owa-mail-store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { formatRelativeDate } from 'owa-observable-datetime';
import { getAnchorForContextMenu } from 'owa-positioning';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';

import {
    getMailListRowDragPreview,
    MAIL_ITEM_DRAG_XOFFSET,
    MAIL_ITEM_DRAG_YOFFSET,
} from '../utils/mailListDragUtil';
import {
    setFocusToSynchronous,
    FocusComponent,
} from 'owa-mail-focus-manager/lib/mailModuleAutoFocusManager';

const styles = require('./MailListItem.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailListItemContainerProps {
    mailListTableProps: MailListTableProps;
    mailListItemDataProps: MailListItemDataProps;
    resetListViewContentTabIndex: () => void;
    isFocusInMailList: () => boolean;
    styleSelectorAsPerUserSettings: string;
}

@observer
export default class MailListItemContainer extends React.Component<MailListItemContainerProps, {}> {
    private lastIsFocused: boolean = false;
    private listViewItem: HTMLDivElement;

    componentWillUnmount() {
        // If item about to be unmounted had focus, set focus to the mail list component immediately so we do not
        // lose focus to body.
        if (this.listViewItem && this.listViewItem.contains(document.activeElement)) {
            setFocusToSynchronous(FocusComponent.MailList);
        }
    }

    @computed
    get isChecked(): boolean {
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);
        return getIsChecked(
            tableView,
            this.props.mailListItemDataProps.rowKey,
            this.props.mailListItemDataProps.lastDeliveryTimestamp
        );
    }

    @computed
    get shouldRowBeFocused(): boolean {
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);

        if (!tableView) {
            return false;
        }

        return tableView.focusedRowKey === this.props.mailListItemDataProps.rowKey;
    }

    @computed
    get shouldShowRow(): boolean {
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);
        const rowKey = this.props.mailListItemDataProps.rowKey;
        const rowKeyIndex = tableView.rowKeys.indexOf(rowKey);

        // Short circuit should show logic if row has not been rendered / user hasn't scrolled before,
        // or the user is in search since the total results will be 250
        // or if the row is within the first 175 rows.
        if (
            !listViewStore.rowInfoForVLV.get(rowKey) ||
            (tableView.tableQuery.type === TableQueryType.Search &&
                !isFeatureEnabled('sea-fetchMax')) ||
            rowKeyIndex < 175
        ) {
            return true;
        }

        const rowKeyOffset = listViewStore.rowInfoForVLV.get(rowKey).rowOffset;
        const viewportHeight = listViewStore.offsetHeight;
        const buffer = 1600; // A pixel buffer on top and below the visible portion of the listview. This is used for virtualization so that if a user scrolls, some items will already be preloaded for them.

        // Otherwise only show if the row is within the user's view (with buffer)
        if (
            rowKeyOffset >= listViewStore.scrollTop - buffer &&
            rowKeyOffset <= viewportHeight + listViewStore.scrollTop + buffer
        ) {
            return true;
        }

        return false;
    }

    get didFocusChange(): boolean {
        return this.lastIsFocused !== this.shouldRowBeFocused;
    }

    private trySetFocus = () => {
        // Check whether focus is in the listview before setting focus on item. There are cases in which focused row
        // might change when the focus is in a different component like compose, in which we do NOT want to focus to
        // go to a mail list item. Example: when in the unread filter, if user clicks on an item and replies, the item
        // disappears from list view and the next item gets selected. If this check wasn't here, then focus
        // would jump from compose to that newly selected mail list item.
        if (this.listViewItem && this.shouldRowBeFocused && this.props.isFocusInMailList()) {
            // Focus on self if ref exists and the item is focused and focus is in mail list already
            // We need state checks here because this is being called async on requestAnimationFrame.
            this.listViewItem.focus();
            this.listViewItem.setAttribute('tabindex', tabIndex.sequentialIndex);

            // Reset listview content tabindex such that listview is
            // not in a sequential keyboard navigation anymore once an item selected
            this.props.resetListViewContentTabIndex();
        }
    };

    private resetTabIndex = () => {
        if (this.listViewItem && !this.shouldRowBeFocused) {
            // Clear tab index on item when it's no longer focused
            // We need state checks here because this is being called async on requestAnimationFrame
            this.listViewItem.setAttribute('tabindex', tabIndex.nonSequentialIndex.toString());
        }
    };

    @keydownHandler('shift+f10', true /*stopPropagation*/)
    openItemContextMenu(evt: React.MouseEvent<HTMLElement>) {
        this.onItemContextMenu(evt);
    }

    /**
     * The onContextMenu event handler which opens the context menu
     * @param evt the mouse event
     */
    private onItemContextMenu = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        evt.preventDefault();

        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);
        const selectedRows = tableView.selectedRowKeys;

        // Select the row upon right click if
        // - user is not in selectAll mode
        // - and the row that clicked on is not selected yet
        if (
            !tableView.isInVirtualSelectAllMode &&
            (!selectedRows || !selectedRows.has(this.props.mailListItemDataProps.rowKey))
        ) {
            onMailListItemClickHandler(
                evt,
                MailListItemSelectionSource.MailListItemContextMenu,
                this.props.mailListItemDataProps.rowKey,
                this.props.mailListTableProps.tableViewId
            );
        }

        showMailItemContextMenu(
            getAnchorForContextMenu(evt),
            MailListContextMenuType.All,
            MailListContextMenuSource.MailListItem
        );
    };

    private updateFocusOnFocusChange = () => {
        if (this.shouldRowBeFocused) {
            window.requestAnimationFrame(this.trySetFocus);
        } else {
            // Only need to clear tab index if an item goes from focused to unfocused
            window.requestAnimationFrame(this.resetTabIndex);
        }

        this.lastIsFocused = this.shouldRowBeFocused;
    };

    private setListViewItemRef = (ref: HTMLDivElement) => {
        this.listViewItem = ref;
    };

    private getLabelForMailItem(): string {
        const {
            unreadCount,
            importance,
            hasAttachment,
            isFlagged,
            isPinned,
            subject,
            sendersOrRecipients,
            lastDeliveryTimestamp,
            thirdLineText,
            showDraftsIndicator,
        } = this.props.mailListItemDataProps;
        const labelArray = [];

        if (unreadCount > 0) {
            labelArray.push(loc(unreadFilter));
        }

        if (importance === 'High') {
            labelArray.push(loc(important));
        }

        if (hasAttachment) {
            labelArray.push(loc(hasAttachments));
        }

        if (isFlagged) {
            labelArray.push(loc(flaggedFilter));
        }

        if (isPinned) {
            labelArray.push(loc(pinnedGroupHeader));
        }

        labelArray.push(sendersOrRecipients);

        if (showDraftsIndicator) {
            labelArray.push(loc(draftSenderPersonaPlaceholder));
        }

        labelArray.push(subject);

        if (!this.props.mailListItemDataProps.returnTime && lastDeliveryTimestamp) {
            labelArray.push(formatRelativeDate(lastDeliveryTimestamp));
        }

        // Only push preview text if it is not null (null when preview text off setting is on)
        if (thirdLineText) {
            labelArray.push(thirdLineText);
        }

        return labelArray.join(' '); // convert into string
    }

    render() {
        // Run focus related logic if selection changes
        const { mailListItemDataProps, mailListTableProps } = this.props;
        const isSelected = mailListItemDataProps.isSelected;

        if (this.didFocusChange) {
            this.updateFocusOnFocusChange();
        }

        const backgroundColorClass = getBackgroundColorClass(
            mailListItemDataProps,
            mailListTableProps,
            this.isChecked
        );

        const unreadItemClass = getUnreadItemClass(
            mailListItemDataProps,
            mailListTableProps,
            this.isChecked,
            styles.unreadMailListItem,
            styles.unreadMailListItemDarker
        );

        const shouldBackgroundChangeOnHover = !isSelected && !this.isChecked;

        // Outer container CSS classes that are shared between 3 column and single line views
        const mailListItemOuterContainerClasses = classNames(
            isFeatureEnabled('tri-density-controls') && !(isBrowserEdge() || isBrowserIE())
                ? styles.mailListItemDensityPadding
                : styles.mailListItemPadding,
            styles.mailListItem,
            backgroundColorClass,
            shouldBackgroundChangeOnHover && styles.backgroundChangeOnHover,
            styles.showHoverActionsOnHover,
            mailListItemDataProps.unreadCount > 0 && unreadItemClass
        );

        const itemAriaProps: AriaProperties = {
            role: AriaRoles.option,
            selected: isSelected,
            label: this.getLabelForMailItem() || null, // When no important information, do not add label
        };
        const isRowVirtualized = isFeatureEnabled('tri-virtualizedListView') && !this.shouldShowRow;
        const height = isRowVirtualized
            ? {
                  height: listViewStore.rowInfoForVLV.get(mailListItemDataProps.rowKey).rowHeight,
              }
            : null;

        return (
            <div
                id={mailListItemDataProps.rowKey}
                data-convid={mailListItemDataProps.conversationId}
                ref={this.setListViewItemRef}
                tabIndex={-1}
                className={
                    isRowVirtualized
                        ? styles.vlvMailListItemContainer
                        : styles.mailListItemContainer
                }
                style={height}
                onClick={this.onClick}
                {...generateDomPropertiesForAria(itemAriaProps)}>
                {!isRowVirtualized && (
                    <Draggable
                        canDrag={this.canDrag}
                        getDragData={this.getDragData}
                        getDragPreview={getMailListRowDragPreview}
                        xOffset={MAIL_ITEM_DRAG_XOFFSET}
                        yOffset={MAIL_ITEM_DRAG_YOFFSET}>
                        <div
                            className={mailListItemOuterContainerClasses}
                            onClick={this.onClick}
                            onDoubleClick={this.onDoubleClick}
                            onContextMenu={this.onItemContextMenu}
                            tabIndex={-1}>
                            <MailListItem
                                mailListTableProps={mailListTableProps}
                                mailListItemDataProps={mailListItemDataProps}
                                styleSelectorAsPerUserSettings={
                                    this.props.styleSelectorAsPerUserSettings
                                }
                            />
                        </div>
                    </Draggable>
                )}
                {!isRowVirtualized && mailListItemDataProps.isExpanded && (
                    <MailListItemExpansion
                        conversationId={mailListItemDataProps.rowId}
                        tableViewId={mailListTableProps.tableViewId}
                        isSingleLine={mailListTableProps.isSingleLine}
                        tableViewSupportsFlagging={mailListTableProps.supportsFlagging}
                        densityMode={mailListTableProps.displayDensityMode}
                        styleSelectorAsPerUserSettings={this.props.styleSelectorAsPerUserSettings}
                    />
                )}
            </div>
        );
    }

    private onClick = evt => {
        const { mailListItemDataProps, mailListTableProps } = this.props;
        onMailListItemClickHandler(
            evt,
            MailListItemSelectionSource.MailListItemBody,
            mailListItemDataProps.rowKey,
            mailListTableProps.tableViewId
        );
    };

    private onDoubleClick = evt => {
        const { mailListItemDataProps, mailListTableProps } = this.props;
        if (mailListTableProps.supportsDoubleClick) {
            onMailListItemClickHandler(
                evt,
                MailListItemSelectionSource.MailListItemBodyDoubleClick,
                mailListItemDataProps.rowKey,
                mailListTableProps.tableViewId
            );
        }
    };

    // In checked mode we have the parent container be draggable
    // Disable dragging if folderId or parentFolderId is of Notes Folder
    private canDrag = () => {
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);
        return (
            !tableView.selectedRowKeys.has(this.props.mailListItemDataProps.rowKey) &&
            this.props.mailListTableProps.canDragFromTable &&
            !this.props.mailListItemDataProps.isInNotesFolder
        );
    };

    private getItemSize = (itemId: string) => {
        const item = mailStore.items.get(itemId);
        return item && item.Size;
    };

    private getDragData = () => {
        const itemProps = this.props.mailListItemDataProps;
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);

        return {
            itemType: DraggableItemTypes.MailListRow,
            rowKeys: [this.props.mailListItemDataProps.rowKey],
            tableViewId: this.props.mailListTableProps.tableViewId,
            tableListViewType: tableView.tableQuery.listViewType,
            subjects: [itemProps.subject ? itemProps.subject : loc(noSubject)],
            sizes: [this.getItemSize(itemProps.latestItemId)],
            latestItemIds: [itemProps.latestItemId],
        };
    };

    @keydownHandler(getCommands().moveToFolder)
    moveToFolder(evt: React.MouseEvent<HTMLElement>) {
        this.moveToShortcutHandler(evt, MailListContextMenuType.MoveTo);
    }

    @keydownHandler(getCommands().categorize)
    moveToCategory(evt: React.MouseEvent<HTMLElement>) {
        this.moveToShortcutHandler(evt, MailListContextMenuType.Categorize);
    }

    @keydownHandler('left')
    collapseMailListItem(evt: React.MouseEvent<HTMLElement>) {
        keyboardCollapseConversation(this.props.mailListItemDataProps.rowKey);
    }

    @keydownHandler('right')
    expandMailListItem(evt: React.MouseEvent<HTMLElement>) {
        if (
            !this.props.mailListItemDataProps.shouldShowTwisty ||
            isMailRowExpanded(this.props.mailListItemDataProps.rowKey)
        ) {
            // no item part to expand or the conversation is already expanded
            return;
        }

        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);
        // perform the same action as clicking on the twisty
        singleSelectRow(
            tableView,
            this.props.mailListItemDataProps.rowKey,
            true /* isUserNavigation */,
            MailListItemSelectionSource.MailListItemTwisty
        );
    }

    private moveToShortcutHandler(
        evt: React.MouseEvent<HTMLElement>,
        ContextMenuType: MailListContextMenuType
    ) {
        const tableView = listViewStore.tableViews.get(this.props.mailListTableProps.tableViewId);

        // moving messages shorcut is disabled for groups
        if (isGroupTableQuery(tableView.tableQuery)) {
            return;
        }

        const selectedRows = tableView.selectedRowKeys;
        if (selectedRows.size > 0) {
            const clientRect = evt.currentTarget.getBoundingClientRect();
            showMailItemContextMenu({ x: clientRect.right, y: clientRect.top }, ContextMenuType);
        }
    }
}
